import React, { useState } from 'react';
import { Button, Space, Table } from 'antd';
import { EyeOutlined, PlusOutlined } from '@ant-design/icons';
import { ColumnsType } from 'antd/es/table';
import { format } from 'date-fns';
import { useNavigate, useParams } from 'react-router-dom';
import withAuthorization from '../../components/hocs/withAuthorization';
import { RealmRoles } from '../../constants';
import PageWithFilters from '../../components/page/page-with-filters';
import { PageWithFiltersContentContainer } from '../../components/page/page.styles';
import { ICardProduct } from '../../interfaces/card-products';
import useGetAllCardProducts from '../../hooks/useGetAllCardProducts';
import dayjs from 'dayjs';

const CardProducts = () => {
  const navigate = useNavigate();
  const { issuer } = useParams<{ issuer: string }>();

  const [paginationModel, setPaginationModel] = useState({
    page: 0,
    pageSize: 10,
  });

  const { cardProducts, pageInfo, isLoading } = useGetAllCardProducts(
    paginationModel.page,
    paginationModel.pageSize,
  );

  const NewCardProductBtn = withAuthorization([RealmRoles.CardProductWrite], () => (
    <Button icon={<PlusOutlined />} onClick={() => navigate(`/${issuer}/new-card-product`) } type="primary">
      Nuevo
    </Button>
  ));


  const handleViewUnnamedLotDetailClick = (e: React.MouseEvent<HTMLElement, MouseEvent>, cardProductId: string) => {
    e.preventDefault();
    navigate(`/${issuer}/card-products/${cardProductId}`);
  };

  const columns: ColumnsType<ICardProduct> = [
    {
      dataIndex: 'id',
      key: 'id',
      title: 'ID',
    },
    {
      key: 'name',
      dataIndex: 'name',
      title: 'Nombre',
      render: (_, record) => {
        return record.name ?? record.name ?? 'Sin nombre.';
      },
    },
    {
      key: 'bin',
      dataIndex: 'bin',
      title: 'Bin',
    },
    {
      key: 'range',
      dataIndex: 'range',
      title: 'Rango',
    },
    {
      key: 'brand',
      dataIndex: 'brand',
      title: 'Marca',
    },
    {
      key: 'product_type',
      dataIndex: 'product_type',
      title: 'Tipo de Producto',
    },
    {
      key: 'start_date',
      dataIndex: ['start_date'],
      title: 'Fecha Desde',
      render: (_, record) => dayjs(record?.start_date).format("DD-MM-YYYY"),
    },
    {
      key: 'end_date',
      dataIndex: ['end_date'],
      title: 'Fecha Hasta',
      render: (_, record) => dayjs(record?.end_date).format("DD-MM-YYYY"),
    },
    {
      key: 'active',
      dataIndex: 'active',
      title: 'Activo',
      render: (_, record) => record?.active ? 'Si' : 'No'
    },
    {
      key: 'actions',
      title: 'Acciones',
      render: (_, record) => (
        <Space size="middle">
          <Button
            onClick={(e) => handleViewUnnamedLotDetailClick(e, record.id)}
            size="small"
            type="link"
            icon={<EyeOutlined />}
          >
            Ver
          </Button>
        </Space>
      ),
    }
  ]

  const handlePaginationChange = (page: number, pageSize: number) => {
    setPaginationModel({ page: page - 1, pageSize });
  };

  return (
    <PageWithFilters title="Card Products">
      <PageWithFiltersContentContainer>
        <div style={{ display: 'flex', marginBottom: '1.5rem', justifyContent: 'flex-end', gap: '1rem' }}>
          <NewCardProductBtn />
        </div>
        <Table
          dataSource={cardProducts ?? []}
          scroll={{ x: true }}
          columns={columns}
          loading={isLoading}
          pagination={{
            total: pageInfo.total,
            defaultPageSize: 10,
            defaultCurrent: 1,
            onChange: (page, pageSize) => {
              setPaginationModel({ page, pageSize });
            },
            pageSizeOptions: ['10', '25', '50', '100'],
            showSizeChanger: true,
          }}
        />
      </PageWithFiltersContentContainer>
    </PageWithFilters>
  );
};

export default withAuthorization([RealmRoles.CardProductWrite, RealmRoles.CardProductRead], CardProducts);
